/* ===== Modern Variant Selectors JavaScript ===== */
/* Prevents selection of variant options that don't have variant IDs */
/* Only disables when variant truly doesn't exist (no variant ID), not when out of stock */
/* NEW: Option1 (first option) is never disabled based on current Option2 selection - only if it has zero variants globally */
/* Option2+ (subsequent options) are disabled based on selected Option1 */
/* Works generically for any product with multiple options (Metal/Size, Color/Size, etc.) */
/* Auto-switches Option2 to first valid value when Option1 changes and current Option2 is invalid */
/*
 * 2026-10-06: one controller per <variant-selection> root (init(root)).
 * Every lookup that used to run against `document` now runs against the
 * root it belongs to, so the same file drives the PDP and the Quick Shop
 * modal (.modal--quickshop-full / -slim), which Empire injects after load.
 *
 *   - PDP: roots present at DOMContentLoaded are initialised 100ms later,
 *     exactly as before.
 *   - Quick Shop: a MutationObserver initialises any <variant-selection>
 *     added later (same 100ms settle), and a capture-phase variant-change
 *     listener catches a root the observer has not seen yet. The observer
 *     callback only initialises roots; status-label writes add spans, never
 *     a <variant-selection>, so it cannot feed itself (the 2026-05-07 loop
 *     came from observers that re-ran markInvalidOptions).
 *   - A WeakMap of controllers guards against double init.
 *   - The document click/mousedown/change listeners are installed once and
 *     resolve their root from the event target; events outside an
 *     initialised root are ignored.
 *
 * Chip stock counts come from the inventory map snippets/of-meta-strip.liquid
 * renders (script[data-of-meta-inventory]), not from badge text. Pure
 * decisions are exported for node:test (tests/variant-selector-core.test.mjs).
 * Loaded only from layout/theme.liquid, deferred.
 */

(function (globalRoot) {
  'use strict';

  // Extract variant title from hidden select option text (format: "Title - $Price")
  // Option values can contain " - " (e.g. "#1 - Scribe/Detailing"), so we must take everything
  // except the last segment (the price), not just the first segment
  function parseVariantTitleFromOptionText(variantText) {
    if (!variantText || typeof variantText !== 'string') return '';
    const parts = variantText.trim().split(' - ');
    if (parts.length > 1) {
      // Last part is the price (e.g. "$15.30"); everything before is the variant title
      return parts.slice(0, -1).join(' - ').trim().toLowerCase();
    }
    return variantText.trim().toLowerCase();
  }

  // Pluralize an option name with special handling for "Size" variants
  function pluralizeOptionName(optionName) {
    if (!optionName) return optionName;
    const lower = optionName.toLowerCase();
    
    // Any option name containing "Size" becomes just "sizes"
    if (lower.includes('size')) {
      return 'sizes';
    }
    
    // Already plural words (ending in s) - check if it's truly plural
    if (/s$/.test(lower) && !/[sxz]|[cs]h$/.test(lower)) {
      // Words like "Metals", "Stones" are already plural
      if (lower === 'metals' || lower === 'stones' || lower === 'metal types') {
        return optionName.toLowerCase();
      }
    }
    
    // Words ending in s, x, z, ch, sh -> add "es"
    if (/[sxz]|[cs]h$/.test(lower)) {
      return optionName.toLowerCase() + 'es';
    }
    // Words ending in y -> replace with "ies"
    if (/y$/.test(lower) && !/[aeiou]y$/.test(lower)) {
      return optionName.toLowerCase().slice(0, -1) + 'ies';
    }
    // Words ending in "ness" -> add "es" (e.g., "hardness" -> "hardnesses")
    if (/ness$/.test(lower)) {
      return optionName.toLowerCase() + 'es';
    }
    // Default: add "s"
    return optionName.toLowerCase() + 's';
  }

  // Chip stock fields from one inventory map entry ({qty, available, policy}).
  // Same four fields the old badge read produced, plus toOrder. Exact stock
  // on every option (2026-10-18): a tracked variant with stock shows its
  // count whatever its policy (the old read, copied from Empire's badge,
  // printed "In stock" with no number for continue-selling variants).
  // Tracked at zero or below with policy continue is toOrder: the website
  // still sells it, but it is not in stock, so the chip reads the label
  // Liquid owns (snippets/of-stock-labels.liquid, data-of-label-to-order on
  // the inventory map), never "In stock". Untracked stays as it was:
  // stockLevelText null, which the chip prints as "In Stock".
  function stockInfoFromInventory(entry) {
    const none = { stockLevelText: null, inventoryQuantity: null, inventoryManagement: null, inventoryPolicy: null, toOrder: false };
    if (!entry || typeof entry.qty !== 'number') return none; // untracked or unknown
    const qty = entry.qty;
    const policy = entry.policy === 'continue' ? 'continue' : 'deny';
    if (qty > 0) {
      return { stockLevelText: qty + ' in stock', inventoryQuantity: qty, inventoryManagement: 'shopify', inventoryPolicy: policy, toOrder: false };
    }
    if (policy === 'continue') {
      return { stockLevelText: null, inventoryQuantity: null, inventoryManagement: 'shopify', inventoryPolicy: policy, toOrder: true };
    }
    return { stockLevelText: 'Out of stock', inventoryQuantity: null, inventoryManagement: 'shopify', inventoryPolicy: policy, toOrder: false };
  }

  // Quick Shop sold-out rule. Option1: sold out only when EVERY variant
  // carrying that value is unavailable (otherwise the auto-switch can still
  // reach an in-stock combination). Option2+: the combination it would make
  // with the current picks exists and is unavailable.
  function isSoldOutChoice(optionIndex, validVariantsForValue, variantInfo) {
    if (optionIndex === 0) {
      return Array.isArray(validVariantsForValue) &&
        validVariantsForValue.length > 0 &&
        validVariantsForValue.every(v => v.disabled);
    }
    return !!(variantInfo && variantInfo.id && variantInfo.isOutOfStock);
  }

  function makeController(root) {
    const isQuickshop = !!root.closest('.modal--quickshop-full, .modal--quickshop-slim');
    // Product scope: .product--outer wraps the gallery and details in
    // snippets/product.liquid on the PDP and in the quickshop section.
    const scope = root.closest('.product--outer') || root.closest('[data-product-wrapper]') || document;

    let inventoryMapCache;
    let toOrderLabel = '';
    function getInventoryMap() {
      if (inventoryMapCache !== undefined) return inventoryMapCache;
      inventoryMapCache = null;
      const el = scope.querySelector('script[data-of-meta-inventory]');
      if (el && el.textContent) {
        try { inventoryMapCache = JSON.parse(el.textContent); } catch (_) { inventoryMapCache = null; }
        // The one "available to order" label, rendered by Liquid from
        // snippets/of-stock-labels.liquid. No fallback words here on purpose.
        toOrderLabel = el.getAttribute('data-of-label-to-order') || '';
      }
      return inventoryMapCache;
    }

    // Fallback for product templates without the meta strip: the original
    // read of Empire's server-rendered stock badge, unchanged.
    function stockInfoFromBadge(variantId) {
      const stockLevelEl = scope.querySelector(`.product-stock-level-wrapper[data-stock-variant-id="${variantId}"]`);
      let stockLevelText = null;
      let inventoryQuantity = null;
      let inventoryManagement = null;
      let inventoryPolicy = null;
      if (stockLevelEl) {
        const badgeTextEl = stockLevelEl.querySelector('.product-stock-level__badge-text');
        if (badgeTextEl) {
          stockLevelText = badgeTextEl.textContent.trim();
          const quantityMatch = stockLevelText.match(/(\d+)/);
          if (quantityMatch) inventoryQuantity = parseInt(quantityMatch[1]);
          if (stockLevelEl.querySelector('.product-stock-level')) {
            inventoryManagement = 'shopify';
            const stockLevelClasses = stockLevelEl.querySelector('.product-stock-level')?.className || '';
            inventoryPolicy = stockLevelClasses.includes('product-stock-level--continue-selling') ? 'continue' : 'deny';
          }
        }
      }
      return { stockLevelText, inventoryQuantity, inventoryManagement, inventoryPolicy };
    }

  
    // Get all valid variants for a specific option value across ALL other options
    // Used to check if Option1 has any valid variants globally
    function getValidVariantsForOptionValue(optionIndex, optionValue) {
      const hiddenSelect = root.querySelector('.variant-selection__variants');
      if (!hiddenSelect || !hiddenSelect.options) return [];
    
      const validVariants = [];
      const optionValueLower = optionValue.toLowerCase();
    
      Array.from(hiddenSelect.options).forEach(opt => {
        if (!opt.value || opt.value === 'not-selected') return;
      
        const variantText = opt.textContent || opt.innerText || '';
        const variantTitleOnly = parseVariantTitleFromOptionText(variantText);
      
        // For single option products, check direct match
        if (optionIndex === 0 && variantTitleOnly === optionValueLower) {
          validVariants.push({
            id: parseInt(opt.value),
            title: variantTitleOnly,
            disabled: opt.disabled
          });
        }
        // For multi-option products, split by " / " and check exact match at the option index
        else {
          const parts = variantTitleOnly.split(' / ');
          // Check if we have enough parts and if the part at optionIndex matches
          if (parts.length > optionIndex && parts[optionIndex] && parts[optionIndex].trim() === optionValueLower) {
            validVariants.push({
              id: parseInt(opt.value),
              title: variantTitleOnly,
              disabled: opt.disabled
            });
          }
        }
      });
    
      return validVariants;
    }
  
    // Check if a variant exists for an option combination by checking the hidden select
    // This is more reliable than data-variant-option-available which may be incorrectly set
    function variantExistsForOption(input) {
      if (!input) return false;
    
      const optionValue = input.value;
      const optionIndex = parseInt(input.dataset.variantOptionValueIndex || input.getAttribute('data-variant-option-value-index'), 10);
    
      // Build the full combination that would result from selecting this option
      const testSelections = [];
      root.querySelectorAll('.options-selection__option-value-input').forEach(opt => {
        const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
        if (idx === optionIndex) {
          testSelections[idx] = optionValue; // Use the option we're checking
        } else if (opt.checked || opt.selected) {
          testSelections[idx] = opt.value;
        }
      });
    
      // Check hidden select - it contains all variants with their IDs
      const hiddenSelect = root.querySelector('.variant-selection__variants');
      if (!hiddenSelect || !hiddenSelect.options) return true; // If no select, enable by default
    
      // Check if the FULL combination exists in variant titles
      // Variant titles are like "8-0 - $100" (single option) or "14K Yellow / 1.5 x 1.0mm - $166.73" (multi-option)
      const combinationExists = Array.from(hiddenSelect.options).some(opt => {
        if (!opt.value || opt.value === 'not-selected') return false;
      
        const variantText = opt.textContent || opt.innerText || '';
      
        // For multi-option products, variant text is "Option1 / Option2 - $Price"
        // We need to check if the EXACT combination exists
        const selectedValues = testSelections.filter(v => v !== undefined && v !== null);
      
        if (selectedValues.length === 0) return true; // No selections yet, enable by default
      
        // Build the expected combination string
        // Format: "Option1 / Option2" (without price)
        const expectedCombination = selectedValues.map(v => v.toLowerCase()).join(' / ');
      
        // Remove price from variant text (option values can contain " - ", e.g. "#1 - Scribe/Detailing")
        const variantTitleOnly = parseVariantTitleFromOptionText(variantText);
      
        // Check if the variant title matches our expected combination exactly
        // This ensures "14K Yellow / 6.0 x 1.5mm" only matches that exact combination,
        // not "14K White / 6.0 x 1.5mm"
        if (variantTitleOnly === expectedCombination) {
          return true;
        }
      
        // Fallback: Check if all values appear in order (for edge cases)
        // This handles cases where the format might be slightly different
        let lastIndex = 0;
        let allFound = true;
        for (let i = 0; i < selectedValues.length; i++) {
          const valueLower = selectedValues[i].toLowerCase();
          const index = variantTitleOnly.indexOf(valueLower, lastIndex);
          if (index === -1) {
            allFound = false;
            break;
          }
          lastIndex = index + valueLower.length;
        }
      
        return allFound;
      });
    
      // If combination exists, variant exists (regardless of stock)
      if (combinationExists) {
        return true;
      }
    
      // Fallback: If data-variant-option-available says false AND we couldn't find the combination,
      // then it truly doesn't exist
      const dataAttrValue = input.dataset.variantOptionAvailable || input.getAttribute('data-variant-option-available');
      if (dataAttrValue === 'false') {
        return false; // Truly doesn't exist
      }
    
      // Default: enable it (conservative approach)
      return true;
    }
  
    // Get variant ID and inventory info for a combination
    function getVariantInfo(input) {
      if (!input) return null;
    
      const optionValue = input.value;
      const optionIndex = parseInt(input.dataset.variantOptionValueIndex || input.getAttribute('data-variant-option-value-index'), 10);
    
      // Build the full combination
      const testSelections = [];
      root.querySelectorAll('.options-selection__option-value-input').forEach(opt => {
        const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
        if (idx === optionIndex) {
          testSelections[idx] = optionValue;
        } else if (opt.checked || opt.selected) {
          testSelections[idx] = opt.value;
        }
      });
    
      const hiddenSelect = root.querySelector('.variant-selection__variants');
      if (!hiddenSelect || !hiddenSelect.options) return null;
    
      // Find the variant option that matches this combination
      const selectedValues = testSelections.filter(v => v !== undefined && v !== null);
      if (selectedValues.length === 0) return null;

      // Another option is still unchosen (only on the no-option-chosen page;
      // everywhere else every option arrives chosen): read every variant this
      // value could still lead to. Before this the partial pair never matched
      // a full title, so every second-option chip read "Unavailable", was
      // marked variant-does-not-exist and refused the click.
      const axisCount = variantAxisCount();
      if (axisCount > 1 && selectedValues.length < axisCount) {
        const inv = getInventoryMap();
        const entries = [];
        Array.from(hiddenSelect.options).forEach(opt => {
          if (!opt.value || opt.value === 'not-selected') return;
          const parts = parseVariantTitleFromOptionText(opt.textContent || opt.innerText || '').split(' / ').map(p => p.trim());
          for (let i = 0; i < testSelections.length; i++) {
            if (testSelections[i] === undefined || testSelections[i] === null) continue;
            if (parts[i] !== String(testSelections[i]).toLowerCase()) return;
          }
          entries.push({ disabled: opt.disabled, inv: inv ? inv[String(opt.value)] : null });
        });
        return partialStockInfo(entries);
      }
    
      // Build expected combination - for single option, just use the value; for multi-option, join with " / "
      const expectedCombination = selectedValues.length === 1 
        ? selectedValues[0].toLowerCase()
        : selectedValues.map(v => v.toLowerCase()).join(' / ');
    
      const matchingOption = Array.from(hiddenSelect.options).find(opt => {
        if (!opt.value || opt.value === 'not-selected') return false;
        const variantText = opt.textContent || opt.innerText || '';
        const variantTitleOnly = parseVariantTitleFromOptionText(variantText);
        return variantTitleOnly === expectedCombination;
      });
    
      if (!matchingOption) return null;
    
      const variantId = parseInt(matchingOption.value);
      const isDisabled = matchingOption.disabled;
    
      // Stock for the chip label. Source of truth is the inventory map that
      // snippets/of-meta-strip.liquid renders from Liquid (qty, available,
      // policy per variant), on the PDP and in Quick Shop alike. Templates
      // without the meta strip have no map; there, and only there, the old
      // read of Empire's server-rendered stock badge is kept so those PDPs
      // render exactly as before.
      let stockInfo = null;
      const inventoryMap = getInventoryMap();
      if (inventoryMap) {
        stockInfo = stockInfoFromInventory(inventoryMap[String(variantId)]);
      } else {
        stockInfo = stockInfoFromBadge(variantId);
      }
      const stockLevelText = stockInfo.stockLevelText;
      const inventoryQuantity = stockInfo.inventoryQuantity;
      const inventoryManagement = stockInfo.inventoryManagement;
      const inventoryPolicy = stockInfo.inventoryPolicy;

      return {
        id: variantId,
        isOutOfStock: isDisabled,
        inventoryQuantity: inventoryQuantity,
        inventoryManagement: inventoryManagement,
        inventoryPolicy: inventoryPolicy,
        stockLevelText: stockLevelText,
        toOrder: !!stockInfo.toOrder
      };
    }
  
    // Check if variant is out of stock (exists but disabled in hidden select)
    function isVariantOutOfStock(input) {
      const variantInfo = getVariantInfo(input);
      return variantInfo ? variantInfo.isOutOfStock : false;
    }
  
  
    // Get Option2's name from the DOM (e.g., "Size", "Stone Size")
    // Cleans common prefixes like "Choose" or "Select"
    function getOption2Name() {
      // Find Option2 option group element (could be fieldset, div, or select wrapper)
      const option2Group = root.querySelector('[data-variant-option-index="1"]');
      if (!option2Group) return null;
    
      // Find the option name element - it should be near the option group
      // Could be in a legend (for fieldset) or label (for select)
      let optionNameEl = null;
    
      // First, try to find it within the same parent container
      const parentContainer = option2Group.closest('.options-selection') || option2Group.closest('.variant-selection');
      if (parentContainer) {
        // Find all option name elements and get the one for Option2 (index 1)
        const allOptionNames = parentContainer.querySelectorAll('[data-variant-option-choose-name]');
        // Option names are typically in order, so the second one should be Option2
        // But we can't rely on order, so we need to find the one associated with Option2
        // The option name is usually in a legend or label near the option group
        optionNameEl = Array.from(allOptionNames).find(el => {
          // Check if this name element is associated with Option2
          // Look for nearby option group with index 1
          const nearbyGroup = el.closest('fieldset, .options-selection__select-wrapper');
          if (nearbyGroup) {
            const nearbyOptionGroup = nearbyGroup.querySelector('[data-variant-option-index="1"]');
            return nearbyOptionGroup !== null;
          }
          return false;
        });
      }
    
      // Fallback: if not found, try finding by traversing up from the option group
      if (!optionNameEl) {
        let current = option2Group.parentElement;
        let depth = 0;
        while (current && !optionNameEl && depth < 5) {
          optionNameEl = current.querySelector('[data-variant-option-choose-name]');
          current = current.parentElement;
          depth++;
        }
      }
    
      // If still not found, try finding all option names and use the second one (assuming order)
      if (!optionNameEl) {
        const allOptionNames = root.querySelectorAll('[data-variant-option-choose-name]');
        if (allOptionNames.length > 1) {
          optionNameEl = allOptionNames[1]; // Second option name should be Option2
        }
      }
    
      if (!optionNameEl) return null;
    
      // Extract and clean name
      const chooseName = optionNameEl.getAttribute('data-variant-option-choose-name') || '';
      // Remove common prefixes like "Choose", "Select", etc.
      let cleaned = chooseName.replace(/^(choose|select)\s+/i, '').trim();
      // Remove articles like "a", "an", "the" at the start
      cleaned = cleaned.replace(/^(a|an|the)\s+/i, '').trim();
      return cleaned || null;
    }
  
    // Build the "opt1 / opt2 / opt3" title that WOULD result from setting one axis to a
    // candidate value while every other axis keeps whatever is checked right now.
    // handleOption1Change used to hardcode a two-part [option1, option2] string and exact
    // compare it against full variant titles. On a three-option product that string can
    // never match a three-part title, so the auto-switch believed the shopper's Option2
    // was invalid on EVERY Option1 click and re-picked it, ignoring Option3 entirely
    // (July review U4-1, money-path F1). Same shape variantExistsForOption/getVariantInfo
    // already build, so a two-option product produces the identical string it did before.
    function buildCombinationFor(optionIndex, optionValue) {
      const selections = [];
      root.querySelectorAll('.options-selection__option-value-input').forEach(opt => {
        const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
        if (isNaN(idx)) return;
        if (idx === optionIndex) {
          selections[idx] = optionValue;
        } else if ((opt.checked || opt.selected) && selections[idx] === undefined) {
          selections[idx] = opt.value;
        }
      });
      const values = selections.filter(v => v !== undefined && v !== null);
      if (!values.length) return null;
      return values.map(v => String(v).toLowerCase()).join(' / ');
    }

    // How many option axes this product actually has, read from the hidden select's
    // variant titles rather than from the radio inputs, because an axis rendered as a
    // dropdown (options-select.liquid) contributes no radios to count.
    function variantAxisCount() {
      const hiddenSelect = root.querySelector('.variant-selection__variants');
      if (!hiddenSelect || !hiddenSelect.options) return 0;
      let max = 0;
      Array.from(hiddenSelect.options).forEach(opt => {
        if (!opt.value || opt.value === 'not-selected') return;
        const parts = parseVariantTitleFromOptionText(opt.textContent || opt.innerText || '').split(' / ');
        if (parts.length > max) max = parts.length;
      });
      return max;
    }

    // Three-or-more-axis auto-switch. Picks a REAL variant under the new Option1 that
    // keeps as many of the shopper's other choices as it can, preferring one that is
    // actually in stock, then returns its full lowercase parts. Two-option products do
    // not come through here; they keep findFirstValidOption2ForOption1 unchanged.
    function findBestCombinationForOption1(option1Value) {
      const hiddenSelect = root.querySelector('.variant-selection__variants');
      if (!hiddenSelect || !hiddenSelect.options) return null;
      const option1Lower = String(option1Value).toLowerCase();

      const current = [];
      root.querySelectorAll('.options-selection__option-value-input').forEach(opt => {
        const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
        if (isNaN(idx) || idx === 0) return;
        if (opt.checked || opt.selected) current[idx] = String(opt.value).toLowerCase();
      });

      let best = null;
      let bestScore = -1;
      Array.from(hiddenSelect.options).forEach(opt => {
        if (!opt.value || opt.value === 'not-selected') return;
        const parts = parseVariantTitleFromOptionText(opt.textContent || opt.innerText || '')
          .split(' / ').map(p => p.trim());
        if (parts.length < 2 || parts[0] !== option1Lower) return;
        // In stock outranks every preserved choice; among equals, more of the shopper's
        // own picks kept wins. Ties fall to variant order, which is the merchant's order.
        let score = opt.disabled ? 0 : 1000;
        for (let i = 1; i < parts.length; i++) {
          if (current[i] && current[i] === parts[i]) score += 10;
        }
        if (score > bestScore) { bestScore = score; best = parts; }
      });
      return best;
    }

    // Move every axis from index 1 up onto the resolved combination, then fire ONE
    // round of events off the last axis touched so Empire resolves the finished
    // combination instead of each intermediate one.
    function applyCombination(parts) {
      const allInputs = Array.from(root.querySelectorAll('.options-selection__option-value-input'));
      let lastChanged = null;
      for (let idx = 1; idx < parts.length; idx++) {
        const axisInputs = allInputs.filter(opt => {
          const i = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
          return i === idx;
        });
        if (!axisInputs.length) continue;
        const target = axisInputs.find(opt => String(opt.value).toLowerCase() === parts[idx]);
        if (!target || target.checked) continue;
        axisInputs.forEach(opt => {
          if (opt === target) return;
          if (opt.checked !== undefined) opt.checked = false;
          opt.removeAttribute('checked');
        });
        if (target.checked !== undefined) target.checked = true;
        target.setAttribute('checked', 'checked');
        target.setAttribute('data-variant-option-available', 'true');
        lastChanged = target;
      }
      if (!lastChanged) return false;
      lastChanged.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
      lastChanged.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
      lastChanged.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
      return true;
    }

    // Find first valid Option2 value for a given Option1 value
    // Works generically for any two-option product (Metal/Size, Color/Size, etc.)
    function findFirstValidOption2ForOption1(option1Value) {
      const hiddenSelect = root.querySelector('.variant-selection__variants');
      if (!hiddenSelect || !hiddenSelect.options) return null;
    
      const option1Lower = option1Value.toLowerCase();
      const validOption2Values = [];
    
      Array.from(hiddenSelect.options).forEach(opt => {
        if (!opt.value || opt.value === 'not-selected') return;
      
        const variantText = opt.textContent || opt.innerText || '';
        const variantTitleOnly = parseVariantTitleFromOptionText(variantText);
      
        // Split by " / " to get option values (variantTitleOnly is already lowercase)
        const parts = variantTitleOnly.split(' / ');
        // Check if first part matches Option1 and we have a second part (Option2)
        if (parts.length >= 2 && parts[0].trim() === option1Lower) {
          const option2Value = parts[1].trim();
          // Store with original case from input if possible, otherwise use lowercase
          if (!validOption2Values.includes(option2Value)) {
            validOption2Values.push(option2Value);
          }
        }
      });
    
      // Return first Option2 value (sorted alphabetically for consistency)
      // Try to preserve original case by matching against actual option values
      if (validOption2Values.length > 0) {
        const sortedValues = validOption2Values.sort();
        const firstValueLower = sortedValues[0];
      
        // Try to find the actual option input with matching value to get original case
        const option2Inputs = Array.from(root.querySelectorAll('.options-selection__option-value-input'));
        const matchingInput = option2Inputs.find(opt => {
          const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
          return idx === 1 && opt.value.toLowerCase() === firstValueLower;
        });
      
        return matchingInput ? matchingInput.value : firstValueLower;
      }
    
      return null;
    }
  
    // Mark options and add status labels
    function markInvalidOptions() {
      const inputs = root.querySelectorAll('.options-selection__option-value-input');
    
      // Get currently selected options
      const selectedOptions = {};
      inputs.forEach(opt => {
        if (opt.checked || opt.selected) {
          const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
          selectedOptions[idx] = opt.value;
        }
      });
    
      inputs.forEach(input => {
        const optionIndex = parseInt(input.dataset.variantOptionValueIndex || input.getAttribute('data-variant-option-value-index'), 10);
        const optionValue = input.value;
      
        // Get or create the status label element.
        // Text chips print the option value inside .options-selection__option-value-name
        // and the status line lives in there with it. Swatches render a fixed 48x48
        // .options-selection__option-swatch-wrapper instead, which has no room for text,
        // so their status line is appended to the <label> and stacks under the swatch.
        // Until 2026-09-03 this gated on the value-name class alone and returned early
        // for every swatch input, so swatch axes were never marked at all: a sold-out or
        // nonexistent swatch rendered and clicked exactly like an available one
        // (July review U4-6, money-path F2).
        const sibling = input.nextElementSibling;
        if (!sibling) return;
        const isSwatch = sibling.classList.contains('options-selection__option-swatch-wrapper');
        if (!isSwatch && !sibling.classList.contains('options-selection__option-value-name')) return;
        const labelHost = isSwatch
          ? (input.closest('.options-selection__option-value-label') || sibling)
          : sibling;

        let statusLabel = labelHost.querySelector('.variant-status-label');
        if (!statusLabel) {
          statusLabel = document.createElement('span');
          statusLabel.className = 'variant-status-label';
          labelHost.appendChild(statusLabel);
        }
      
        // NEW LOGIC: Different handling for Option1 (first option) vs Option2+ (subsequent options)
        // This works for any product with multiple options (Metal/Size, Color/Size, etc.)
        if (optionIndex === 0) {
          // Option1 (first option): Only disable if it has ZERO valid variants across ALL other options
          const validVariants = getValidVariantsForOptionValue(0, optionValue);
          const hasAnyValidVariants = validVariants.length > 0;
        
          if (!hasAnyValidVariants) {
            // Option1 has no variants at all - disable it
            input.classList.add('variant-does-not-exist');
            statusLabel.textContent = 'Unavailable';
            statusLabel.className = 'variant-status-label variant-status-label--unavailable';
            statusLabel.style.display = 'block';
          } else {
            // Option1 has variants - always enable it, but show status for current combination
            input.classList.remove('variant-does-not-exist');
            const variantInfo = getVariantInfo(input);
          
            if (variantInfo && variantInfo.id) {
              // Current combination exists - show inventory status
              if (variantInfo.isOutOfStock) {
                statusLabel.textContent = 'Out of Stock';
                statusLabel.className = 'variant-status-label variant-status-label--out-of-stock';
                statusLabel.style.display = 'block';
              } else if (variantInfo.toOrder && toOrderLabel) {
                statusLabel.textContent = toOrderLabel;
                statusLabel.className = 'variant-status-label variant-status-label--to-order';
                statusLabel.style.display = 'block';
              } else if (variantInfo.stockLevelText) {
                if (variantInfo.inventoryQuantity !== null && variantInfo.inventoryQuantity !== undefined && variantInfo.inventoryQuantity > 0) {
                  statusLabel.textContent = `${variantInfo.inventoryQuantity} in stock`;
                  statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                  statusLabel.style.display = 'block';
                } else {
                  statusLabel.textContent = 'In Stock';
                  statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                  statusLabel.style.display = 'block';
                }
              } else {
                statusLabel.textContent = 'In Stock';
                statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                statusLabel.style.display = 'block';
              }
            } else {
              // Current combination doesn't exist, but Option1 has other variants - show "Other [option2 name] available"
              const option2Name = getOption2Name();
              let labelText = 'other sizes available';
              if (option2Name) {
                const pluralName = pluralizeOptionName(option2Name);
                labelText = `other ${pluralName} available`;
              }
              statusLabel.textContent = labelText;
              // Use orange color (rgb(233, 145, 20) / #E99114) to indicate limited availability in this combination
              statusLabel.className = 'variant-status-label variant-status-label--other-available';
              statusLabel.style.display = 'block';
            }
          }
        } else {
          // Option2+ (subsequent options): Disable if it doesn't exist for the currently selected Option1
          const variantInfo = getVariantInfo(input);
        
          if (!variantInfo || !variantInfo.id) {
            // No variant exists for this combination - show "Unavailable"
            input.classList.add('variant-does-not-exist');
            statusLabel.textContent = 'Unavailable';
            statusLabel.className = 'variant-status-label variant-status-label--unavailable';
            statusLabel.style.display = 'block';
          } else if (variantInfo.isOutOfStock) {
            // Variant exists but is out of stock - show "Out of Stock"
            input.classList.remove('variant-does-not-exist');
            statusLabel.textContent = 'Out of Stock';
            statusLabel.className = 'variant-status-label variant-status-label--out-of-stock';
            statusLabel.style.display = 'block';
          } else {
            // Variant exists and is in stock - show inventory status
            input.classList.remove('variant-does-not-exist');

            // Use the stock level text from the PDP if available (most reliable)
            if (variantInfo.toOrder && toOrderLabel) {
              statusLabel.textContent = toOrderLabel;
              statusLabel.className = 'variant-status-label variant-status-label--to-order';
              statusLabel.style.display = 'block';
            } else if (variantInfo.stockLevelText) {
              if (variantInfo.inventoryQuantity !== null && variantInfo.inventoryQuantity !== undefined && variantInfo.inventoryQuantity > 0) {
                statusLabel.textContent = `${variantInfo.inventoryQuantity} in stock`;
                statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                statusLabel.style.display = 'block';
              } else if (variantInfo.stockLevelText.toLowerCase().includes('out of stock') || 
                         variantInfo.stockLevelText.toLowerCase().includes('no stock')) {
                statusLabel.textContent = 'Out of Stock';
                statusLabel.className = 'variant-status-label variant-status-label--out-of-stock';
                statusLabel.style.display = 'block';
              } else {
                statusLabel.textContent = 'In Stock';
                statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                statusLabel.style.display = 'block';
              }
            } else if (variantInfo.inventoryManagement) {
              const inventoryQty = variantInfo.inventoryQuantity !== null && variantInfo.inventoryQuantity !== undefined 
                                   ? Number(variantInfo.inventoryQuantity) : 0;
            
              if (variantInfo.inventoryPolicy === 'continue') {
                statusLabel.textContent = 'In Stock';
                statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                statusLabel.style.display = 'block';
              } else if (inventoryQty > 0) {
                statusLabel.textContent = `${inventoryQty} in stock`;
                statusLabel.className = 'variant-status-label variant-status-label--in-stock';
                statusLabel.style.display = 'block';
              } else {
                statusLabel.textContent = 'Out of Stock';
                statusLabel.className = 'variant-status-label variant-status-label--out-of-stock';
                statusLabel.style.display = 'block';
              }
            } else {
              statusLabel.textContent = 'In Stock';
              statusLabel.className = 'variant-status-label variant-status-label--in-stock';
              statusLabel.style.display = 'block';
            }
          }
        }

        // A sold-out choice is khaki, its value struck through, with a muted
        // caption (approved mock B in Quick Shop, 2026-10-06; the PDP too
        // since 2026-10-18, Rhett's call). Quick Shop also refuses the pick;
        // the PDP keeps sold-out chips selectable (settings.sold_out_options
        // = selectable), so a sold-out pick there leads to the muted Sold out
        // button and the wishlist primary instead.
        if (!isSwatch) wrapChipValue(sibling);
        markSoldOut(input, optionIndex, optionValue);
      });
    }

    // Put the chip's value text in its own span so the sold-out strike can
    // sit on the value and not on the stock caption under it (a line-through
    // on the chip itself propagates to every in-flow child). Runs once per
    // chip; Empire re-inserts the same wrapper nodes on every options update,
    // so the span survives.
    function wrapChipValue(nameEl) {
      if (nameEl.querySelector(':scope > .of-qs-chip-value')) return;
      const textNode = Array.prototype.find.call(nameEl.childNodes, n => n.nodeType === 3 && n.textContent.trim());
      if (!textNode) return;
      const span = document.createElement('span');
      span.className = 'of-qs-chip-value';
      span.textContent = textNode.textContent.trim();
      nameEl.replaceChild(span, textNode);
    }

    // The class is the source of truth for the sold-out look and, in Quick
    // Shop, the click guards. Quick Shop sets disabled too (it also keeps
    // arrow keys off the radio), but Empire's options-selection writes
    // disabled = false on every options update when disable-unavailable is
    // off, so init re-marks after Empire's own first update and the
    // variant-change listener re-marks after each later one. On the PDP the
    // input is never disabled: the chip stays selectable and keyboard
    // reachable (decision 1, 2026-10-18), the class only drives the look.
    function markSoldOut(input, optionIndex, optionValue) {
      const soldOut = isSoldOutChoice(
        optionIndex,
        optionIndex === 0 ? getValidVariantsForOptionValue(0, optionValue) : null,
        optionIndex === 0 ? null : getVariantInfo(input)
      );
      if (soldOut) {
        input.classList.add('of-variant-soldout');
        if (isQuickshop) {
          input.disabled = true;
          input.setAttribute('aria-disabled', 'true');
        }
      } else if (input.classList.contains('of-variant-soldout')) {
        // Only undo what this function did; never re-enable an input someone
        // else disabled.
        input.classList.remove('of-variant-soldout');
        if (isQuickshop) {
          input.disabled = false;
          input.removeAttribute('aria-disabled');
        }
      }
    }
  
    // Handle auto-switching Option2 when Option1 changes
    // Works generically for any two-option product (Metal/Size, Color/Size, etc.)
    function handleOption1Change(option1Input) {
      const option1Value = option1Input.value;
      const optionIndex = parseInt(option1Input.dataset.variantOptionValueIndex || option1Input.getAttribute('data-variant-option-value-index'), 10);
    
      if (optionIndex !== 0) return; // Only handle Option1 (first option)
    
      // Check if current Option2 is valid for this Option1
      const currentOption2Input = Array.from(root.querySelectorAll('.options-selection__option-value-input')).find(opt => {
        const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
        return idx === 1 && (opt.checked || opt.selected);
      });
    
      if (currentOption2Input) {
        // Build the test combination from EVERY axis the shopper has chosen, not a
        // hardcoded [option1, option2] pair. For a two-option product this is the same
        // two-part string as before; for a three-option product it is the three-part
        // string that can actually match a variant title (July U4-1 / money-path F1).
        const expectedCombination = buildCombinationFor(0, option1Value);

        const hiddenSelect = root.querySelector('.variant-selection__variants');
        const combinationExists = !!expectedCombination && hiddenSelect && Array.from(hiddenSelect.options).some(opt => {
          if (!opt.value || opt.value === 'not-selected') return false;
          const variantText = opt.textContent || opt.innerText || '';
          const variantTitleOnly = parseVariantTitleFromOptionText(variantText);
          return variantTitleOnly === expectedCombination;
        });

        // If the combination the shopper is now on does not exist, move to one that does.
        if (!combinationExists) {
          // Three or more axes: Option2 alone cannot decide validity, because whatever
          // Option2 we pick still has to agree with Option3. Resolve the whole tail at
          // once instead. The two-option path below is deliberately left as it was.
          if (variantAxisCount() > 2) {
            const best = findBestCombinationForOption1(option1Value);
            if (best) applyCombination(best);
            return;
          }

          const firstValidOption2 = findFirstValidOption2ForOption1(option1Value);
        
          if (firstValidOption2) {
            // Find the Option2 input with matching value (case-insensitive)
            const option2Input = Array.from(root.querySelectorAll('.options-selection__option-value-input')).find(opt => {
              const idx = parseInt(opt.dataset.variantOptionValueIndex || opt.getAttribute('data-variant-option-value-index'), 10);
              return idx === 1 && opt.value.toLowerCase() === firstValidOption2.toLowerCase();
            });
          
            if (option2Input && option2Input !== currentOption2Input) {
              // Uncheck current Option2
              if (currentOption2Input.checked !== undefined) {
                currentOption2Input.checked = false;
              }
              currentOption2Input.removeAttribute('checked');
              currentOption2Input.setAttribute('data-variant-option-available', 'false');
            
              // Check new Option2
              if (option2Input.checked !== undefined) {
                option2Input.checked = true;
              }
              option2Input.setAttribute('checked', 'checked');
              option2Input.setAttribute('data-variant-option-available', 'true');
            
              // Trigger events to update variant selection
              const changeEvent = new Event('change', { bubbles: true, cancelable: true });
              option2Input.dispatchEvent(changeEvent);
            
              const clickEvent = new Event('click', { bubbles: true, cancelable: true });
              option2Input.dispatchEvent(clickEvent);
            
              // Also trigger input event for compatibility
              const inputEvent = new Event('input', { bubbles: true, cancelable: true });
              option2Input.dispatchEvent(inputEvent);
            }
          }
        }
      }
    }

    function optionIndexOf(input) {
      return parseInt(input.dataset.variantOptionValueIndex || input.getAttribute('data-variant-option-value-index'), 10);
    }

    return {
      root,
      isQuickshop,
      markInvalidOptions,
      handleOption1Change,
      variantExistsForOption,
      getValidVariantsForOptionValue,
      optionIndexOf
    };
  }

  // A chip on a product where another option is still unchosen (the
  // no-option-chosen product page, snippets/of-nopick.liquid, 2026-10-18).
  // There is no single variant to read, so the chip speaks for every variant
  // it could still lead to: entries are those variants, each { disabled
  // (the hidden select's sold-out flag), inv (its inventory map entry) }.
  // None: the value leads nowhere (null, the caller says Unavailable). All
  // sold out: Out of Stock. Otherwise In Stock when one of them is on the
  // shelf (tracked with stock, or untracked), else the available to order
  // label. No count: a sum across combinations the shopper has not chosen
  // would not be the stock of anything they can buy.
  function partialStockInfo(entries) {
    if (!Array.isArray(entries) || !entries.length) return null;
    const open = entries.filter(e => !e.disabled);
    const base = { id: 'partial', isOutOfStock: false, inventoryQuantity: null, inventoryManagement: null, inventoryPolicy: null, stockLevelText: null, toOrder: false };
    if (!open.length) return Object.assign(base, { isOutOfStock: true });
    const onShelf = open.some(e => !e.inv || typeof e.inv.qty !== 'number' || e.inv.qty > 0);
    return onShelf ? base : Object.assign(base, { toOrder: true, inventoryManagement: 'shopify', inventoryPolicy: 'continue' });
  }

  const core = { parseVariantTitleFromOptionText, pluralizeOptionName, stockInfoFromInventory, isSoldOutChoice, partialStockInfo };

  if (typeof module === 'object' && module.exports) {
    module.exports = core;
    return;
  }
  if (typeof document === 'undefined') return;
  if (globalRoot.__OFVariantSelectors) return; // a second copy of this file is a no-op
  globalRoot.__OFVariantSelectors = core;

  const controllers = new WeakMap();
  let booted = false;
  let globalsInstalled = false;

  function controllerFor(el) {
    const vs = el && el.closest ? el.closest('variant-selection') : null;
    return vs ? controllers.get(vs) || null : null;
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    return false;
  }

  // Installed once. Same listeners, phases and order as the original
  // single-root version; each resolves its controller from the event target.
  function installGlobalListeners() {
    if (globalsInstalled) return;
    globalsInstalled = true;

    // Prevent clicks on options that don't have variant IDs (capture phase,
    // ahead of Empire's own handlers).
    document.addEventListener('click', function(e) {
      const label = e.target.closest('.options-selection__option-value-label');
      if (!label) return;
      const ctrl = controllerFor(label);
      if (!ctrl) return;

      const input = label.querySelector('.options-selection__option-value-input');
      if (!input) return;

      // Quick Shop sold-out choice: never selectable (the PDP has none).
      if (ctrl.isQuickshop && input.classList.contains('of-variant-soldout')) return stop(e);

      const optionIndex = ctrl.optionIndexOf(input);
      const optionValue = input.value;

      // Option1 is always clickable if it has any valid variants globally.
      if (optionIndex === 0) {
        const validVariants = ctrl.getValidVariantsForOptionValue(0, optionValue);
        if (validVariants.length === 0) return stop(e);
        return true;
      }

      // Option2+: the combination must exist.
      if (!ctrl.variantExistsForOption(input)) return stop(e);
    }, true);

    document.addEventListener('mousedown', function(e) {
      const label = e.target.closest('.options-selection__option-value-label');
      if (!label) return;
      const ctrl = controllerFor(label);
      if (!ctrl) return;

      const input = label.querySelector('.options-selection__option-value-input');
      if (!input) return;
      if (input.classList.contains('variant-does-not-exist')) return stop(e);
      if (ctrl.isQuickshop && input.classList.contains('of-variant-soldout')) return stop(e);
    }, true);

    // Option1 change: auto-switch Option2, then re-mark. Option2+ change:
    // re-mark immediately.
    document.addEventListener('change', function(e) {
      const input = e.target;
      if (!input.classList || !input.classList.contains('options-selection__option-value-input')) return;
      const ctrl = controllerFor(input);
      if (!ctrl) return;

      const optionIndex = ctrl.optionIndexOf(input);

      if (optionIndex === 0 && (input.checked || input.selected)) {
        setTimeout(() => {
          ctrl.handleOption1Change(input);
          ctrl.markInvalidOptions();
        }, 50);
      } else if (optionIndex !== 0) {
        ctrl.markInvalidOptions();
      }
    });

    // Option1 click: same auto-switch after a delay.
    document.addEventListener('click', function(e) {
      const label = e.target.closest('.options-selection__option-value-label');
      if (!label) return;
      const ctrl = controllerFor(label);
      if (!ctrl) return;

      const input = label.querySelector('.options-selection__option-value-input');
      if (!input) return;

      if (ctrl.optionIndexOf(input) === 0) {
        setTimeout(() => {
          if (input.checked || input.selected) {
            ctrl.handleOption1Change(input);
            ctrl.markInvalidOptions();
          }
        }, 100);
      }
    });

    // A variant-change from a root nobody initialised yet (Quick Shop opened
    // faster than the observer settle). variant-change does not bubble, but
    // capture-phase listeners on document still see it.
    document.addEventListener('variant-change', function(e) {
      if (!booted) return;
      const vs = e.target && e.target.closest ? e.target.closest('variant-selection') : null;
      if (vs && !controllers.has(vs)) init(vs);
    }, true);
  }

  function init(root) {
    if (!root || controllers.has(root)) return; // double-init guard
    const ctrl = makeController(root);
    controllers.set(root, ctrl);
    ctrl.markInvalidOptions();

    // Quick Shop: Empire's options-selection runs its first options update
    // when the product JSON resolves, and that update clears disabled on
    // every radio. getProduct() hands back the same promise it chained on,
    // so this re-mark lands after Empire's update.
    if (ctrl.isQuickshop && typeof root.getProduct === 'function') {
      Promise.resolve(root.getProduct()).then(function() {
        requestAnimationFrame(function() { ctrl.markInvalidOptions(); });
      }, function() {});
    }

    // Re-mark when Empire fires variant-change (covers programmatic variant
    // switches, e.g. from URL param load or external code calling
    // variantSelection.variant = id).
    // See notes/memos/2026-05-07-empire-pdp-variant-architecture.md.
    root.addEventListener('variant-change', function() {
      ctrl.markInvalidOptions();
    });
  }

  function scheduleInit(root) {
    if (controllers.has(root)) return;
    // Same 100ms settle the PDP has always had, so Empire's options-selection
    // and ProductDetails (started in a requestAnimationFrame by
    // ProductQuickshop) have set their attributes first.
    setTimeout(() => {
      if (root.isConnected) init(root);
    }, 100);
  }

  function boot() {
    // Run after a short delay to ensure theme has set the attributes
    setTimeout(() => {
      installGlobalListeners();
      document.querySelectorAll('variant-selection').forEach(init);
      booted = true;

      // Quick Shop injects <variant-selection> after load.
      new MutationObserver(function(records) {
        for (let r = 0; r < records.length; r++) {
          const added = records[r].addedNodes;
          for (let i = 0; i < added.length; i++) {
            const n = added[i];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'VARIANT-SELECTION') {
              scheduleInit(n);
            } else if (n.getElementsByTagName('variant-selection').length) {
              Array.prototype.forEach.call(n.getElementsByTagName('variant-selection'), scheduleInit);
            }
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Removed 2026-05-07 (see notes/memos/2026-05-07-empire-pdp-variant-architecture.md):
  //   - MutationObserver on .options-selection: its own callback wrote status-label
  //     classes, which the observer saw as new mutations, which re-fired the
  //     callback. Playwright recorded 1155 status-label mutations per chip click
  //     within 9 seconds (3559 in the subtree). Self-fueling loop.
  //   - MutationObserver on .variant-selection__variants: same write-triggers-
  //     observe pattern; not needed because variant-change fires first.
  //   - setInterval(markInvalidOptions, 500): 5+ calls/sec forever on every PDP.
  // The observer added 2026-10-06 is not that pattern: it only initialises
  // roots it has never seen and never calls markInvalidOptions itself.
})(typeof window !== 'undefined' ? window : this);
